import { motion } from 'motion/react';
import { DEFAULT_USER_STATUS } from '@allemnii-pulse/shared';
import { Avatar, AvatarFallback, AvatarImage } from './ui/Avatar.js';
import { AvatarWithStatus, StatusDot } from './UserStatus.js';
import { cn } from '../lib/utils.js';
import { formatUserName, initialsFor, isSystemActor, useUserProfile } from '../lib/users.js';

/**
 * Resolves a bare uid (audit-log actor, task assignee, comment author, ...) to a human name —
 * "First L." per the product's display rule, see lib/users.ts — instead of ever showing the raw
 * id. Fades in once the profile document has loaded. Carries the person's live status (see
 * components/UserStatus.tsx) — on the avatar's edge, or as a dot before the name without one.
 */
export function UserName({
  uid,
  avatar = false,
  hideName = false,
  avatarClassName,
  showStatus = true,
  className,
}: {
  uid: string;
  avatar?: boolean;
  /** Renders just the avatar, with the name available as a title tooltip instead of inline text —
   * for compact rows (timeline bars, dense lists) where there's no room for "First L." next to it. */
  hideName?: boolean;
  avatarClassName?: string;
  /** Off for static output, e.g. a printed export, where a live status means nothing. */
  showStatus?: boolean;
  className?: string;
}) {
  const { data: profile, loading } = useUserProfile(uid);
  const name = formatUserName(uid, profile);
  const withStatus = showStatus && !isSystemActor(uid);
  // Null until the profile loads, so nobody flashes "offline" on the way in.
  const status = loading ? null : (profile?.status ?? DEFAULT_USER_STATUS);

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} title={hideName ? name : undefined}>
      {avatar && (
        <AvatarWithStatus status={withStatus ? status : null}>
          <Avatar className={avatarClassName}>
            {profile?.photoURL && <AvatarImage src={profile.photoURL} alt="" />}
            <AvatarFallback>{initialsFor(uid, profile)}</AvatarFallback>
          </Avatar>
        </AvatarWithStatus>
      )}
      {!avatar && withStatus && status && <StatusDot status={status} />}
      <motion.span
        key={loading ? 'loading' : name}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
        className={hideName ? 'sr-only' : undefined}
      >
        {name}
      </motion.span>
    </span>
  );
}
